<template>
	<div class="button-container">
		<button
			:type="type"
			:aria-disabled="disabled"
			:aria-label="ariaLabel || label"
			:tabindex="(disabled || loading) ? -1 : undefined"
			class="btn"
			:data-color="color"
			:data-dark-mode="darkMode"
			:data-hover-effect="hoverEffect"
			:data-is-disabled="disabled"
			:data-is-loading="loading"
			:data-is-outline="outline"
			:data-text-mode="textMode"
			:style="style ?? `--color: var(--color-${color}); --dark-color: var(--color-${color}-alt)`"
			:title="title || label"
			role="button"
			@click="onClick">
			<div class="icon-loading" v-show="loading">
				<loading-button-indicator class="mr-1"></loading-button-indicator>
			</div>
			<template v-if="icon">
				<template v-if="!loading">
				<span class="icon">
					<cfl-icon :icon="icon"></cfl-icon>
				</span>
				</template>
			</template>
			<template v-if="slots.icon">
				<template v-if="!loading">
				<span class="icon">
					<slot name="icon"></slot>
				</span>
				</template>
			</template>
			<template v-if="label">
				<span class="label" :id="labelId" v-html="label"></span>
			</template>
			<template v-if="iconSuffix">
				<span class="icon">
					<cfl-icon :icon="iconSuffix"></cfl-icon>
				</span>
			</template>
			<template v-if="keyboardShortcut">
				<span aria-hidden="true" class="shortcut-badge">
					<keyboard-shortcut
						:enter-shortcut="keyboardShortcut === 'enter'"
						:backspace-shortcut="keyboardShortcut === 'backspace'"
						:control="keyboardShortcutControl"
						:shift="keyboardShortcutShift"
						:label-shortcut="!['enter', 'backspace'].includes(keyboardShortcut) ? keyboardShortcut : undefined">
					</keyboard-shortcut>
				</span>
			</template>
		</button>
		<template v-if="badge || badgeIndicator">
			<div class="badge">{{ badge }}</div>
		</template>
		<template v-if="tts && isTtsEnabled && label">
			<div class="button-tts"
			     aria-label="Lire le texte"
			     role="button"
			     tabindex="0"
			     :style="style ?? `--color: var(--color-${color}); --dark-color: var(--color-${color}-alt)`"
			     @click.stop="speakLabel"
			     @keydown.enter.stop="speakLabel"
			     @keydown.space.stop="speakLabel">
				<cfl-icon icon="accessibility-tts"></cfl-icon>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
	import ColorType from "@shared-types/color.type";
	import IconType from "@shared-types/icon.type";
	import CflIcon from "@shared-components-cfl/cfl-icon.vue";
	import LoadingButtonIndicator from "@shared-components/loading/loading-button-indicator.vue";
	import { inject, Ref, Slots, unref, useSlots } from "vue";
	import useTts from "@shared-composables/accessibility/useTts";
	import KeyboardShortcut from "@shared-components/accessibility/keyboard-shortcut.vue";
	import { hapticInjectionKey } from "@shared-utils/accessibility/haptic";

	const props = withDefaults(defineProps<{
		type?: "button" | "submit" | "reset",
		label?: string,
		/** Nom accessible quand le libellé visible est trop court pour se comprendre seul (« Voir »).
		 *  Doit contenir le libellé, sinon le critère WCAG 2.5.3 tombe. */
		ariaLabel?: string,
		title?: string,
		color?: ColorType | any,
		style?: any,
		icon?: IconType,
		iconSuffix?: IconType,
		disabled?: boolean,
		loading?: boolean | Ref<boolean>,
		outline?: boolean,
		darkMode?: boolean,
		textMode?: boolean,
		hoverEffect?: boolean,
		tts?: boolean,
		keyboardShortcut?: string,
		keyboardShortcutControl?: boolean,
		keyboardShortcutShift?: boolean,
		/** Désactive le haptic de ce bouton (ex. quand un parent cliquable porte déjà le haptic). */
		haptics?: boolean,
		/** Badge sur le bouton : valeur affichée (nombre/texte), ou simple pastille via badgeIndicator. */
		badge?: number | string | null,
		badgeIndicator?: boolean,
	}>(), {
		type: "button",
		color: "transparent",
		disabled: false,
		loading: false,
		darkMode: false,
		textMode: false,
		hoverEffect: true,
		tts: true,
		haptics: true,
	});

	const slots = useSlots() as Slots;
	const emit = defineEmits<{
		click: [ event: MouseEvent ]
	}>();

	// Haptic au click (leaf) : cfl-button n'a pas de `:disabled` natif → on garde le buzz pour les états
	// actifs uniquement. Web/admin ne fournissent pas le hook → no-op. Dédup par geste côté provider.
	const triggerHaptic = inject(hapticInjectionKey, () => {});
	const onClick = (event: MouseEvent): void =>
	{
		if (props.haptics && ! props.disabled && ! unref(props.loading))
		{
			void triggerHaptic();
		}
		emit("click", event);
	};

	const { labelId, isTtsEnabled, speakLabel } = useTts("btn-label");
</script>

<style scoped>
	@reference "@main-css";

	.button-container {
		--btn-px: 12px;
		--btn-py: 4px;
		--btn-gap: 8px;
		--btn-rounded: 8px;
		--btn-font-size: 16px;
		--btn-min-h: 44px;
		--btn-icon-size: 16px;
		--btn-icon-only-size: 18px;
		--btn-label-lh: 16px;
		--btn-label-min-h: 18px;

		@variant tablet {
			--btn-font-size: 18px;
			--btn-icon-size: 18px;
			--btn-min-h: 50px;
			--btn-gap: 12px;
			--btn-px: 16px;
		}

		@variant accessibility-font-size-150 {
			--btn-px: 14px;
			--btn-py: 6px;
			--btn-gap: 10px;
			--btn-font-size: 20px;
			--btn-min-h: 48px;
			--btn-icon-size: 20px;
			--btn-icon-only-size: 22px;
			--btn-label-lh: 20px;
			--btn-label-min-h: 22px;

			@variant tablet {
				--btn-min-h: 54px;
				--btn-font-size: 22px;
				--btn-icon-size: 22px;
			}
		}

		@variant accessibility-font-size-200 {
			--btn-px: 16px;
			--btn-py: 8px;
			--btn-gap: 12px;
			--btn-font-size: 24px;
			--btn-min-h: 52px;
			--btn-icon-size: 24px;
			--btn-icon-only-size: 28px;
			--btn-label-lh: 24px;
			--btn-label-min-h: 28px;

			@variant tablet {
				--btn-min-h: 60px;
				--btn-font-size: 26px;
				--btn-icon-size: 26px;
			}
		}

		@apply relative flex flex-row flex-nowrap items-center justify-center;

		.badge {
			@apply badge-pill absolute -top-1.5 -right-1.5 z-10;
		}

		.btn {
			@apply no-underline shrink-0 grow-0;
			@apply gap-(--btn-gap) text-(length:--btn-font-size) px-(--btn-px) py-(--btn-py);
			@apply relative rounded-(--btn-rounded) font-button inline-flex justify-center items-center;
			@apply active:scale-95 active:opacity-70;
			@apply focus-visible:element-kbd-focus;
			@apply accessibility-hover-on-focus accessibility-hover-on-focus:ring-offset-4 accessibility-hover-on-focus:ring-offset-black;
			@apply h-auto max-w-full w-fit min-h-(--btn-min-h) min-w-(--btn-min-h);

			transition-duration: .2s;
			transition-timing-function: ease-in-out;
			transition-property: transform, opacity;

			@media (hover: hover) {
				&:hover {
					@apply opacity-70 cursor-pointer;
				}
			}

			&:deep(.icon) {
				@apply flex items-center justify-center shrink-0;

				[data-icon] {
					width: var(--btn-icon-size);
					height: var(--btn-icon-size);
					color: inherit;
				}

				svg {
					width: var(--btn-icon-size);
					height: var(--btn-icon-size);
					fill: currentColor;
				}

				&:empty {
					@apply hidden;
				}

				&:not(:has(+ .label:not(:empty))) {
					[data-icon], svg {
						width: var(--btn-icon-only-size);
						height: var(--btn-icon-only-size);
					}
				}
			}

			.icon-loading {
				&:deep(svg) {
					width: var(--btn-icon-size);
					height: var(--btn-icon-size);
				}
			}

			&[data-hover-effect="false"] {
				@apply hover:cursor-default hover:opacity-100;
			}

			&[data-is-outline="false"] {
				@apply bg-(--color);
				@apply text-white;

				&[data-dark-mode="true"] {
					@apply dark:bg-(--dark-color);
					@apply dark:text-black;

					.icon {
						@apply dark:fill-black;
					}
				}
			}

			&[data-is-outline="true"] {
				@apply border bg-gray-2/70 dark:bg-dark-2/70;
				@apply border-[var(--color)];
				@apply text-[var(--color)] dark:text-(--dark-color);
				@apply transition-colors;

				&[data-hover-effect="true"] {
					@media (hover: hover) {
						&:hover {
							@apply opacity-100;
							@apply bg-[var(--color)] text-white;

							&[data-dark-mode="true"] {
								@apply dark:bg-(--dark-color) dark:text-black;
							}
						}
					}
				}
			}

			&[data-text-mode="true"] {
				@apply border-0 bg-transparent;
				@apply text-black dark:text-white;
				padding-inline: 0;

				&[data-dark-mode="true"] {
					@apply dark:text-(--dark-color);
				}
			}


			.label {
				@apply self-center text-left content-center select-none;
				/*@apply truncate;*/
				@apply whitespace-normal;
				line-height: var(--btn-label-lh);
				min-height: var(--btn-label-min-h);
			}

			/* Wrapper aria-hidden du badge de raccourci (décoratif → hors nom accessible, WCAG 2.5.3).
			 * display: contents → le kbd reste l'item flex, aucun impact layout. */
			.shortcut-badge {
				display: contents;
			}

			&[data-is-loading="true"],
			&[data-is-disabled="true"] {
				@apply pointer-events-none opacity-60!;
			}

			&[data-is-outline="false"]:not([data-text-mode="true"]) {
				&:deep(.keyboard-shortcut) {
					@apply border-white/20 bg-white/65 text-black/80;
					@apply dark:border-black/20 dark:bg-black/65 dark:text-white/80;
				}
			}
		}

		.button-tts {
			@apply relative rounded-r-(--btn-rounded) font-button inline-flex justify-center items-center shrink-0;
			@apply h-auto max-w-full;
			@apply active:scale-95 active:opacity-70;
			@apply bg-(--color);
			@apply focus-visible:element-kbd-focus;

			gap: var(--btn-gap);
			font-size: var(--btn-font-size);
			padding-inline: var(--btn-px);
			padding-block: var(--btn-py);
			min-height: var(--btn-min-h);
			transition-duration: .2s;
			transition-timing-function: ease-in-out;
			transition-property: transform, opacity;

			[data-icon] {
				width: var(--btn-icon-size);
				height: var(--btn-icon-size);
				@apply text-white;
			}

			@media (hover: hover) {
				&:hover {
					@apply opacity-70 cursor-pointer;
				}
			}
		}

		&:has(.button-tts) {
			.btn {
				@apply rounded-l-(--btn-rounded) rounded-r-none;
			}
		}

		&:has(.btn[data-dark-mode="true"]) {
			.button-tts {
				@apply dark:bg-(--dark-color);

				[data-icon] {
					@apply dark:text-black;
				}
			}
		}

		&:has(.btn[data-text-mode="true"]) {
			.button-tts {
				@apply bg-transparent;

				[data-icon] {
					@apply text-black dark:text-white;
				}
			}
		}

		/* Quand le label est en lecture, ajoute un indicateur visuel */

		&:has(.label.is-playing) {
			.button-tts {
				[data-icon] {
					@apply animate-pulse;
				}
			}
		}
	}
</style>
